import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Crown } from "lucide-react";
import { paymentStatus } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const sessionId = params.get("session_id");

  const poll = useCallback(async (n) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const r = await paymentStatus(sessionId);
      if (r.payment_status === "paid") {
        await refresh();
        setState("paid");
        return;
      }
    } catch { /* keep polling */ }
    if (n >= MAX_POLLS) { setState("timeout"); return; }
    setTimeout(() => poll(n + 1), 2000);
  }, [sessionId, refresh]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="grid min-h-screen place-items-center bg-background px-6 text-center text-white">
      <div className="max-w-md">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-accent" />
            <h1 className="mt-6 font-display text-2xl tracking-wide">Confirming your upgrade…</h1>
            <p className="mt-2 font-body text-sm text-muted-foreground">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-accent/15">
              <Crown className="h-8 w-8 text-accent" />
            </div>
            <h1 className="mt-6 font-display text-3xl tracking-wide">You're Pro now 🎉</h1>
            <p className="mt-2 font-body text-sm text-muted-foreground">
              Watermarks off, unlimited shows, 1080p exports and bulk downloads unlocked.
            </p>
            <button onClick={() => navigate("/")} data-testid="success-continue"
                    className="mt-8 bg-accent px-6 py-3 font-body text-sm font-semibold text-black">
              Back to the studio
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-6 font-display text-2xl tracking-wide">Still processing</h1>
            <p className="mt-2 font-body text-sm text-muted-foreground">
              Your payment may take a moment to confirm. Your plan will update automatically.
            </p>
            <button onClick={() => navigate("/")} data-testid="success-continue"
                    className="mt-8 border border-border px-6 py-3 font-body text-sm text-white">
              Back to the studio
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="grid min-h-screen place-items-center bg-background px-6 text-center text-white">
      <div className="max-w-md">
        <CheckCircle2 className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-6 font-display text-2xl tracking-wide">No worries</h1>
        <p className="mt-2 font-body text-sm text-muted-foreground">
          Checkout was cancelled — you're still on the Free plan. Upgrade whenever you're ready.
        </p>
        <button onClick={() => navigate("/")} data-testid="cancel-continue"
                className="mt-8 bg-accent px-6 py-3 font-body text-sm font-semibold text-black">
          Back to the studio
        </button>
      </div>
    </div>
  );
}
